<!--
  Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
  This information is confidential and proprietary to GM Global Technology
  Operations LLC and may not be used, modified, copied or distributed.
  -->

<br/>
<h1 class="page-header">Aggregator Signal Table</h1>

@if (permission()) {
  <div class="aggregator-signal-page">
    @if (saving || exporting) {
      <p-progressBar mode="indeterminate" [style]="{ height: '6px' }"></p-progressBar>
    }

    <div class="context-row">
      <label>Object Code</label>
      <p-select [options]="browseObjectCodes()" [(ngModel)]="browseObjectCode"
                [showClear]="!!browseObjectCodeSignal()"
                [disabled]="browseSelectorsDisabled()"
                placeholder="Object Code" (onChange)="onBrowseObjectCodeChanged()"></p-select>
      <label>Architecture</label>
      <p-select [options]="browseArchitecturesForObjectCode()" [(ngModel)]="browseArchitecture"
                [showClear]="!!browseArchitectureSignal()"
                [disabled]="browseSelectorsDisabled() || !browseObjectCodeSignal()"
                placeholder="Architecture" (onChange)="onBrowseArchitectureChanged()"></p-select>
      <label>Power Type</label>
      <p-select [options]="browsePowerTypesForObjectCode()" [(ngModel)]="browsePowerType"
                [showClear]="!!browsePowerTypeSignal()"
                [disabled]="browseSelectorsDisabled() || !browseObjectCodeSignal() || !browseArchitectureSignal()"
                placeholder="ICE / EV" (onChange)="onBrowsePowerTypeChanged()"></p-select>
      @if (browseKeywordVisible()) {
        <label>Keyword</label>
        @if (browseKeywordReadonly()) {
          <input class="keyword-readonly" [value]="browseKeywordSignal()" disabled/>
        } @else {
          <p-select [options]="browseKeywordOptions()" [(ngModel)]="browseKeyword"
                    [showClear]="keywordShowClear(browseKeywordSignal(), browseKeywordOptions())"
                    [disabled]="browseSelectorsDisabled()"
                    placeholder="Keyword" (onChange)="onBrowseKeywordChanged()"></p-select>
        }
      }
    </div>

    <div class="button-row" data-testid="table-actions">
      @if (adminPermission()) {
        <p-button label="Create New Table" icon="pi pi-plus"
                  class="mr-2"
                  [disabled]="!canOpenCreateDialog() || saving"
                  (click)="openCreateDialog()"></p-button>
        @if (hasActiveTable()) {
          <p-button label="Delete Table" icon="pi pi-trash" severity="danger"
                    class="mr-2"
                    [disabled]="!canDeleteTable() || saving"
                    (click)="confirmDeleteTable()"></p-button>
        }
        <p-button label="Download All History" icon="pi pi-history"
                  class="mr-2"
                  [disabled]="!canExportHistoryAll() || exporting"
                  (click)="exportHistoryAll()"></p-button>
      }
      <p-button label="Download ICD Compare" icon="pi pi-file-excel"
                [disabled]="!canExportIcdCompare() || saving || exporting"
                (click)="exportIcdCompare()"
                (keydown)="onIcdCompareKeydown($event)"></p-button>
    </div>
    @if (adminPermission() && hasActiveTable()) {
      <div class="button-row" data-testid="edit-actions">
        <p-button label="Reset Changes" icon="pi pi-replay" severity="warn"
                  class="mr-2"
                  [disabled]="!dataChangedSignal() || saving"
                  (click)="resetChanges()"></p-button>
        <p-button label="Save" icon="pi pi-save"
                  [disabled]="!dataChangedSignal() || saving || rowsInvalid"
                  [loading]="saving"
                  (click)="save()"></p-button>
      </div>
    }

    @if (objectCode && architecture && powerType) {
      <div class="editing-matrix-label">
        <strong>{{ contextLabelPrefix }}</strong>
        {{ objectCode + ' - ' + architecture + ' - ' + powerType + (keyword ? ' - ' + keyword : '') }}
      </div>
    }

    @if (adminPermission()) {
      <p-dialog header="Create New Table"
                [(visible)]="createDialogVisible"
                [modal]="true"
                [style]="{ width: '44rem', maxWidth: '95vw' }"
                [contentStyle]="{ overflow: 'visible' }"
                (onHide)="closeCreateDialog()">
        <div class="create-dialog-body">
          <label>Object Code</label>
          <p-autoComplete [(ngModel)]="dialogObjectCode"
                          [suggestions]="filteredCreateObjectCodes"
                          [forceSelection]="true"
                          [dropdown]="true"
                          dropdownMode="blank"
                          [showClear]="true"
                          [disabled]="createObjectCodesLoading"
                          placeholder="Type to search A-prefix object codes"
                          styleClass="create-dialog-field"
                          appendTo="body"
                          (completeMethod)="filterCreateObjectCodes($event)"></p-autoComplete>
          <label>Architecture</label>
          <p-select [options]="dialogArchitectureOptions" [(ngModel)]="dialogArchitecture"
                    placeholder="Architecture"
                    styleClass="create-dialog-field"
                    appendTo="body"></p-select>
          <label>Power Type</label>
          <p-select [options]="dialogPowerTypeOptions" [(ngModel)]="dialogPowerType"
                    placeholder="ICE / EV"
                    styleClass="create-dialog-field"
                    appendTo="body"></p-select>
          <label>Keyword</label>
          <input pInputText [(ngModel)]="dialogKeyword"
                 class="create-dialog-field"
                 maxlength="100"
                 placeholder="Optional"/>
          <label>Copy from</label>
          <p-select [options]="allKeyLabels()" [(ngModel)]="dialogCopySourceLabel"
                    placeholder="Existing key to copy from…"
                    styleClass="create-dialog-field"
                    appendTo="body"></p-select>
        </div>
        <ng-template pTemplate="footer">
          <p-button label="Cancel" severity="secondary" (click)="closeCreateDialog()"></p-button>
          <p-button label="Create" (click)="confirmCreateDialog()"></p-button>
        </ng-template>
      </p-dialog>
    }

    <p-confirmDialog key="deleteTableConfirmation" esciConfirmEnterAccept defaultFocus="accept"></p-confirmDialog>
    <p-confirmDialog key="deleteRowConfirmation" esciConfirmEnterAccept defaultFocus="accept"></p-confirmDialog>
    <div class="component-catalog-grid-wrapper">
      <ag-grid-angular class="ag-theme-balham componentCatalog" style="width:100%; height:65vh"
                       [columnDefs]="cols" [rowData]="rowDataSignal()" [defaultColDef]="defaultColDef"
                       [rowClassRules]="rowClassRules"
                       [pagination]="true"
                       [paginationPageSize]="paginationPageSize"
                       [paginationPageSizeSelector]="paginationPageSizeSelector"
                       (cellEditingStopped)="cellEditingStopped($event, rowData)"
                       (cellValueChanged)="onCellValueChanged($event)"
                       (gridReady)="onGridReady($event)">
      </ag-grid-angular>
    </div>
  </div>
}
